import type { Customer } from "@/mocks/admin/customers";
import StatusBadge from "@/pages/customers/components/StatusBadge";

const typeLabel: Record<Customer["type"], { label: string; icon: string }> = {
  individual: { label: "Individual", icon: "ri-user-line" },
  business: { label: "Business", icon: "ri-building-line" },
  "property-mgmt": { label: "Property Management", icon: "ri-community-line" },
};

type Props = {
  customer: Customer;
  onEdit: () => void;
  onMessage: () => void;
  onNewProject: () => void;
};

export default function CustomerHero({
  customer,
  onEdit,
  onMessage,
  onNewProject,
}: Props) {
  const t = typeLabel[customer.type];

  return (
    <div className="bg-white border border-ink-100 rounded-lg p-5 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {/* Identity */}
        <div className="flex items-center gap-4 min-w-0">
          <div
            className={`w-16 h-16 md:w-20 md:h-20 rounded-xl ${customer.color} text-white text-xl md:text-2xl font-display font-bold flex items-center justify-center shrink-0`}
          >
            {customer.initials}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-xl md:text-2xl font-bold text-ink-900 leading-tight">
                {customer.name}
              </h1>
              <StatusBadge status={customer.status} />
            </div>
            <div className="text-xs text-ink-500 mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-mono font-bold text-ink-400">{customer.id}</span>
              <span className="text-ink-300">·</span>
              <span className="inline-flex items-center gap-1">
                <i className={`${t.icon} text-ink-400`}></i>
                {t.label}
              </span>
              <span className="text-ink-300">·</span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-map-pin-line text-ink-400"></i>
                {customer.city}, {customer.state}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          <a
            href={`tel:${customer.phone}`}
            title="Call customer"
            className="w-9 h-9 flex items-center justify-center rounded-md border border-ink-200 bg-white text-ink-700 hover:bg-cream-100 cursor-pointer"
          >
            <i className="ri-phone-line"></i>
          </a>
          <button
            onClick={onMessage}
            title="Message customer"
            className="w-9 h-9 flex items-center justify-center rounded-md border border-ink-200 bg-white text-ink-700 hover:bg-cream-100 cursor-pointer"
          >
            <i className="ri-mail-send-line"></i>
          </button>
          <button
            onClick={onEdit}
            title="Edit profile"
            className="w-9 h-9 flex items-center justify-center rounded-md border border-ink-200 bg-white text-ink-700 hover:bg-cream-100 cursor-pointer"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            onClick={onNewProject}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            New Project
          </button>
        </div>
      </div>
    </div>
  );
}